import { useEffect, useState, useCallback, useMemo } from "react";
import {
    Modal,
    Form,
    Input,
    Button,
    message,
    Divider,
    Row,
    Col,
    Upload,
    Tooltip,
    Tag,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useStore } from "../../../root-store-context";
import { useAuth } from "react-oidc-context";
import CorrigoConstants, { ALLOWED_DOC_EXTENSIONS_STRING } from "../../../Constants/CorrigoConstants";

const { TextArea } = Input;

export default function WorkOrderModal({
    customerId,
    open,
    onCancel,
    assetId, // kept because other code may depend on it
    workOrderStore,
    onSubmitSuccess,
}) {
    const auth = useAuth();
    const authProfile = auth?.user?.profile;
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [fileList, setFileList] = useState([]);
    const { applicationStore, maintenanceRequestStore } = useStore();

    // constants (kept same identifiers as original)
    const DEFAULT_ASSET_ID = CorrigoConstants.DEFAULT_ASSET_ID;
    const DEFAULT_TASK_ID = CorrigoConstants.DEFAULT_TASK_ID;
    const WORK_ORDER_TYPE_ID = CorrigoConstants.WORK_ORDER_TYPE_ID;
    const WORK_ORDER_SUBTYPE_ID = CorrigoConstants.WORK_ORDER_SUBTYPE_ID;
    const RESIDENT_KEY_CUSTOM_FIELD_ID = CorrigoConstants.CUSTOM_FIELDS.RESIDENT_KEY;
    const USER_ID_CUSTOM_FIELD_ID = CorrigoConstants.CUSTOM_FIELDS.USER_ID;
    const COMMUNITY_ID_CUSTOM_FIELD_ID = CorrigoConstants.CUSTOM_FIELDS.COMMUNITY_ID;
    const RESIDENT_ADDRESS_CUSTOM_FIELD_ID = CorrigoConstants.CUSTOM_FIELDS.RESIDENT_ADDRESS;
    const ACTOR_ID = CorrigoConstants.ACTOR_ID;
    const ADMIN_USER_ID = CorrigoConstants.ADMIN_USER_ID;

    const COLOR_THEME = "#355168ff";
    const MAX_UPLOAD_BYTES = CorrigoConstants.MAX_UPLOAD_SIZE_MB * 1024 * 1024;

    const ALLOWED_EXTENSIONS_SET = useMemo(
        () => new Set((CorrigoConstants.ALLOWED_DOC_EXTENSIONS || []).map((e) => String(e).toLowerCase())),
        []
    );

    const formatPhone = useCallback((raw) => {
        if (!raw) return "";
        const digits = String(raw).replace(/\D/g, "").slice(0, 10);
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return digits.replace(/(\d{3})(\d+)/, "$1-$2");
        return digits.replace(/(\d{3})(\d{3})(\d+)/, "$1-$2-$3");
    }, []);

    const validateBeforeUpload = useCallback(
        (file) => {
            const name = file?.name || "";
            const dotIndex = name.lastIndexOf(".");
            const ext = dotIndex >= 0 ? name.slice(dotIndex + 1).toLowerCase() : "";

            if (!ext || !ALLOWED_EXTENSIONS_SET.has(ext)) {
                message.error(`File "${name}" is not a supported format.`);
                return Upload.LIST_IGNORE;
            }

            if (file.size > MAX_UPLOAD_BYTES) {
                message.error(`File "${name}" exceeds the maximum size of ${CorrigoConstants.MAX_UPLOAD_SIZE_MB} MB.`);
                return Upload.LIST_IGNORE;
            }

            // return false so Ant does not auto-upload but still adds file to list
            return false;
        },
        [ALLOWED_EXTENSIONS_SET]
    );

    const onUploadChange = useCallback(
        ({ fileList: newList = [] }) => {
            const filtered = newList.filter((f) => {
                const fileObj = f?.originFileObj || f;
                if (!fileObj) return false;
                const name = fileObj.name || "";
                const dotIndex = name.lastIndexOf(".");
                const ext = dotIndex >= 0 ? name.slice(dotIndex + 1).toLowerCase() : "";
                if (!ALLOWED_EXTENSIONS_SET.has(ext)) return false;
                if (fileObj.size > MAX_UPLOAD_BYTES) return false;
                return true;
            });
            setFileList(filtered);
        },
        [ALLOWED_EXTENSIONS_SET]
    );

    useEffect(() => {
        if (!open) return undefined;
        let mounted = true;
        const load = async () => {
            try {
                form.resetFields();
                setFileList([]);

                if (!maintenanceRequestStore?.getCommunityResidentDirectoryProfile) return;

                try {
                    const firstName = authProfile?.given_name || "";
                    const lastName = authProfile?.lastName || "";
                    const email = authProfile?.email || "";

                    let profile = null;
                    if (authProfile?.userId !== ADMIN_USER_ID && applicationStore?.SelectedAddress?.residentKey > 0) {
                        profile = await maintenanceRequestStore.getCommunityResidentDirectoryProfile();
                        if (!mounted) return;
                    }

                    const phone = formatPhone(profile?.DaytimePhone || "");

                    const valuesToSet = {};
                    if (firstName) valuesToSet.contactFirstName = firstName;
                    if (lastName) valuesToSet.contactLastName = lastName;
                    if (phone) valuesToSet.contactPhone = phone;
                    if (email) valuesToSet.contactEmail = email;

                    if (Object.keys(valuesToSet).length > 0) form.setFieldsValue(valuesToSet);
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn("getCommunityResidentDirectoryProfile failed", err);
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("WorkOrderModal load error", err);
                message.error("Failed to prepare form");
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, [open, form, maintenanceRequestStore, formatPhone, authProfile, ADMIN_USER_ID]);

    const fileToBase64 = useCallback((file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => {
                reader.abort();
                reject(new Error("Problem reading file"));
            };
            reader.onload = () => {
                const result = reader.result || "";
                const comma = result.indexOf(",");
                const base64 = comma >= 0 ? result.slice(comma + 1) : result;
                resolve(base64);
            };
            reader.readAsDataURL(file);
        });
    }, []);

    const buildCreateWorkOrderPayload = useCallback(
        async (values, filesArg) => {
            const items = [
                {
                    asset: { id: DEFAULT_ASSET_ID },
                    task: { id: DEFAULT_TASK_ID },
                    comment: values?.description ?? "",
                },
            ];

            const documents = [];
            for (const f of (filesArg || [])) {
                const fileObj = f?.originFileObj || f;
                if (!fileObj) continue;
                try {
                    const title =
                        fileObj.name.length > CorrigoConstants.MAX_TITLE_LENGTH
                            ? fileObj.name.substring(0, CorrigoConstants.MAX_TITLE_LENGTH)
                            : fileObj.name;
                    const base64 = await fileToBase64(fileObj);
                    documents.push({
                        title: title,
                        description: fileObj.name,
                        isShared: false,
                        isPublic: true,
                        storageTypeId: "Cloud",
                        docUrl: null,
                        docType: { displayAs: "File" },
                        actorId: ACTOR_ID,
                        actorTypeId: "WO",
                        blob: { body: base64, fileName: fileObj.name },
                    });
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn("fileToBase64 failed", fileObj?.name, err);
                }
            }

            const contactAddress = {
                addrTypeId: "Contact",
                address: [values?.contactPhone, values?.contactEmail].filter(Boolean).join(","),
            };

            return {
                command: {
                    workOrder: {
                        items,
                        contactName: (values?.contactFirstName + " " + values?.contactLastName) ?? "",
                        contactAddress,
                        typeCategory: WORK_ORDER_TYPE_ID,
                        subType: { id: WORK_ORDER_SUBTYPE_ID },
                        customer: { id: customerId ?? null },
                        documents,
                        customFields: [
                            {
                                value:
                                    (applicationStore?.SelectedAddress?.residentKey ?? 0) == 0
                                        ? 0
                                        : applicationStore?.SelectedAddress?.residentKey.toString(),
                                descriptor: { id: RESIDENT_KEY_CUSTOM_FIELD_ID },
                            },
                            {
                                value: applicationStore?.SelectedAddress?.userId
                                    ? applicationStore?.SelectedAddress?.userId.toString()
                                    : null,
                                descriptor: { id: USER_ID_CUSTOM_FIELD_ID },
                            },
                            {
                                value: applicationStore?.SelectedAddress?.communityId
                                    ? applicationStore?.SelectedAddress?.communityId.toString()
                                    : null,
                                descriptor: { id: COMMUNITY_ID_CUSTOM_FIELD_ID },
                            },
                            {
                                value: applicationStore?.SelectedAddress?.address
                                    ? applicationStore?.SelectedAddress?.address.toString()
                                    : null,
                                descriptor: { id: RESIDENT_ADDRESS_CUSTOM_FIELD_ID },
                            },
                        ],
                    },
                    computeSchedule: true,
                    computeAssignment: true,
                },
            };
        },
        [
            customerId,
            fileToBase64,
            applicationStore,
            DEFAULT_ASSET_ID,
            DEFAULT_TASK_ID,
            ACTOR_ID,
            WORK_ORDER_TYPE_ID,
            WORK_ORDER_SUBTYPE_ID,
            RESIDENT_KEY_CUSTOM_FIELD_ID,
            USER_ID_CUSTOM_FIELD_ID,
            COMMUNITY_ID_CUSTOM_FIELD_ID,
            RESIDENT_ADDRESS_CUSTOM_FIELD_ID,
        ]
    );

    const handleSubmit = useCallback(async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            const files = (fileList || []).map((f) => f?.originFileObj || f).filter(Boolean);
            const payload = await buildCreateWorkOrderPayload(values, files);
            const res = await workOrderStore.createWorkOrder(payload);

            if (!res || res?.status !== 200) {
                throw new Error("Failed to create work order");
            }

            message.success("Work Order created successfully");
            onSubmitSuccess?.(res?.data ?? res);

            form.resetFields();
            setFileList([]);
            onCancel?.();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Failed to create work order", err);
            const messageText = err?.response?.data || err?.message || "Failed to create work order";
            const shown =
                typeof messageText === "string" && messageText.length > 300 ? `${messageText.slice(0, 300)}...` : messageText;
            message.error(shown);
        } finally {
            setSubmitting(false);
        }
    }, [buildCreateWorkOrderPayload, fileList, form, onCancel, onSubmitSuccess, workOrderStore]);

    const resetAndClose = () => {
        form.resetFields();
        setFileList([]);
        onCancel?.();
    };

    return (
        <Modal
            title="Create Work Order"
            open={open}
            onCancel={resetAndClose}
            width={700}
            destroyOnClose
            footer={[
                <Button
                    key="cancel"
                    onClick={resetAndClose}
                >
                    Cancel
                </Button>,
                <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
                    Create
                </Button>,
            ]}
        >
            <Form style={{ marginTop: 50 }} layout="vertical" form={form}>
                <Tag
                    style={{
                        display: "block",           // make Tag fill available width
                        width: "100%",              // ensure it respects container width
                        boxSizing: "border-box",    // include padding in width
                        borderRadius: 6,
                        background: "#eef3f6",
                        color: "#215575",
                        border: "1px solid rgba(90,120,144,0.12)",
                        padding: "8px 12px",
                        fontSize: 14,
                        lineHeight: 1.6,
                        whiteSpace: "normal",       // allow wrapping
                        overflowWrap: "break-word", // break long words if necessary
                        wordBreak: "break-word"
                    }}
                >
                    {/* use a span (no default margins) instead of <p> */}
                    <span>
                        Please complete this form to request maintenance in your community. Please note - AAM Maintenance Requests do not cover areas owned by municipalities or individual owners. If you have any questions, please contact your Community Manager.
                    </span>
                </Tag>

                <Divider />
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item
                            label="First Name"
                            name="contactFirstName"
                            rules={[{ required: true, message: "First Name is required" }]}
                        >
                            <Input
                                placeholder="John"
                                onChange={(e) => {
                                    const raw = String(e.target.value || "");
                                    const clean = raw.replace(/[^a-zA-Z\s'-]/g, "");
                                    form.setFieldsValue({ contactFirstName: clean });
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Last Name"
                            name="contactLastName"
                            rules={[{ required: true, message: "Last Name is required" }]}
                        >
                            <Input
                                placeholder="Doe"
                                onChange={(e) => {
                                    const raw = String(e.target.value || "");
                                    const clean = raw.replace(/[^a-zA-Z\s'-]/g, "");
                                    form.setFieldsValue({ contactLastName: clean });
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item
                            label="Contact Phone"
                            name="contactPhone"
                            rules={[
                                { required: true, message: "Phone number is required" },
                                { pattern: /^\d{3}-\d{3}-\d{4}$/, message: "Phone must be in the format 602-957-9191" },
                            ]}
                        >
                            <Input
                                maxLength={12}
                                placeholder="602-957-9191"
                                onChange={(e) => {
                                    let value = String(e.target.value || "");
                                    value = value.replace(/\D/g, "");
                                    if (value.length > 3 && value.length <= 6) value = value.replace(/(\d{3})(\d+)/, "$1-$2");
                                    else if (value.length > 6) value = value.replace(/(\d{3})(\d{3})(\d+)/, "$1-$2-$3");
                                    form.setFieldsValue({ contactPhone: value });
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Contact Email"
                            name="contactEmail"
                            rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
                        >
                            <Input
                                placeholder="john@example.com"
                                onChange={(e) => {
                                    const raw = String(e.target.value || "");
                                    const clean = raw.replace(/[^a-zA-Z0-9@._-]/g, "");
                                    form.setFieldsValue({ contactEmail: clean });
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider />

                <Form.Item
                    label="Description"
                    name="description"
                    rules={[{ required: true, message: "Description is required" }]}
                >
                    <TextArea rows={8} placeholder="Describe the issue or request..." />
                </Form.Item>

                <Divider />

                <Form.Item label="Attachments">
                    <Upload.Dragger
                        multiple
                        beforeUpload={validateBeforeUpload}
                        fileList={fileList}
                        onChange={onUploadChange}
                        style={{ backgroundColor: "#eef3f4ff", borderRadius: 8 }}
                    >
                        <div style={{ marginTop: 6, fontSize: 12, color: COLOR_THEME }}>
                            <p>
                                <strong>Max upload size:</strong> {CorrigoConstants.MAX_UPLOAD_SIZE_MB} MB{" "}
                                <Tooltip title={ALLOWED_DOC_EXTENSIONS_STRING}>
                                    <span style={{ cursor: "pointer", textDecoration: "underline" }}>
                                        (Allowed file formats)
                                    </span>
                                </Tooltip>
                            </p>
                        </div>

                        <p className="ant-upload-drag-icon">
                            <InboxOutlined style={{ color: COLOR_THEME }} />
                        </p>
                        <p className="ant-upload-text" style={{ color: COLOR_THEME }}>Click or drag files to this area to upload</p>
                        <p className="ant-upload-hint" style={{ color: COLOR_THEME }}>Files will be saved when you click Create</p>
                    </Upload.Dragger>
                </Form.Item>
            </Form>
        </Modal>
    );
}
